/**
 * tokenManager.ts - 토큰 관리 구현
 * 
 * TokenContext와 연동하여 토큰을 관리합니다.
 */

import { apiClient } from './axiosConfig';

// 토큰 저장소 키
const ACCESS_TOKEN_KEY = 'accessToken';
const AUTH_ACCESS_TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const AUTH_REFRESH_TOKEN_KEY = 'auth_refresh_token';

// 동시 refresh 요청 dedup (race condition 방지)
let refreshInFlight: Promise<string> | null = null;

// 401 을 받은 뒤 다시 시도하기까지 기다리는 시간 (다른 탭의 열쇠 교체가 끝나기를 기다림)
const REFRESH_RETRY_DELAY_MS = 1200;

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/** 서버가 리프레시 열쇠를 거절했는가 (= 진짜 로그아웃 사유) */
const isAuthRejection = (error: any): boolean => {
  const status = error?.response?.status;
  return status === 401 || status === 403;
};

/**
 * 로그아웃해야 하는 실패인지 표시한다. 인터셉터는 이 표시가 있을 때만 로그인 화면으로 보낸다.
 * (표시가 없으면 일시적 오류 → 그 요청만 실패시키고 로그인은 유지)
 */
const markAuthFailure = <T,>(error: T): T => {
  try {
    (error as any).authFailure = true;
  } catch (_) {
    // 표시를 못 붙여도 동작엔 지장 없음
  }
  return error;
};

export const isRefreshAuthFailure = (error: any): boolean => !!error?.authFailure;

// 토큰 관리자 구현
export const tokenManager = {
  getAccessToken: (): string | null => {
    // auth_access_token 우선, accessToken은 호환성 유지
    return localStorage.getItem(AUTH_ACCESS_TOKEN_KEY) || localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  getRefreshToken: (): string | null => {
    // auth_refresh_token 우선, refreshToken은 호환성 유지
    return localStorage.getItem(AUTH_REFRESH_TOKEN_KEY) || localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setTokens: (accessToken: string, refreshToken: string, expiresAt?: string | number): void => {
    // 두 키 모두에 저장 (호환성 유지)
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken);

    // ⭐ 만료 시각 저장 — 명시 값 → JWT exp 클레임 → fallback 순
    try {
      let expiresAtIso: string | null = null;
      if (expiresAt) {
        expiresAtIso = typeof expiresAt === 'number'
          ? new Date(expiresAt * 1000).toISOString()
          : new Date(expiresAt).toISOString();
      } else if (accessToken && accessToken.split('.').length === 3) {
        // JWT 페이로드에서 exp 추출
        const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload?.exp) {
          expiresAtIso = new Date(payload.exp * 1000).toISOString();
        }
      }
      // 최종 fallback: 1시간
      if (!expiresAtIso) {
        expiresAtIso = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      }
      localStorage.setItem('auth_token_expires_at', expiresAtIso);
    } catch (_) {
      // 디코드 실패해도 fallback 으로 1시간 저장
      localStorage.setItem('auth_token_expires_at',
        new Date(Date.now() + 60 * 60 * 1000).toISOString());
    }
  },

  clearTokens: (): void => {
    // 두 키 모두 삭제
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(AUTH_ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  },

  // 모든 토큰 관련 키 삭제 (로그인 전 정리용)
  clearAllTokens: (): void => {
    const tokenKeys = [
      'accessToken',
      'auth_access_token',
      'refreshToken',
      'auth_refresh_token',
      'auth_token_expires_at',
      'auth_user_data',
      'auth_session_data',
    ];
    tokenKeys.forEach(key => localStorage.removeItem(key));
  },

  refreshAccessToken: async (): Promise<string> => {
    // 이미 refresh 진행 중이면 그 Promise 재사용 (중복 호출 방지)
    if (refreshInFlight) {
      return refreshInFlight;
    }

    const sentToken = tokenManager.getRefreshToken();
    if (!sentToken) {
      throw markAuthFailure(new Error('No refresh token available'));
    }

    const requestRefresh = (token: string) =>
      apiClient.post('/api/v1/auth/refresh', { refresh_token: token }, { _retry: true } as any);

    refreshInFlight = (async () => {
      try {
        let response;
        try {
          response = await requestRefresh(sentToken);
        } catch (firstError) {
          // 🔴 2026-09-24 우장산점 '매일 13시 로그아웃' — 서버 지연·타임아웃 한 번에 로그아웃되면 안 된다.
          //    인증 거절(401/403)이 아니면 토큰을 지우지 않고 그대로 실패만 돌려준다.
          if (!isAuthRejection(firstError)) throw firstError;

          // 401 이어도 곧바로 로그아웃하지 않는다. 같은 계정을 연 다른 탭이 방금 열쇠를 바꿨을 수 있다.
          // 잠깐 기다린 뒤 저장소의 '지금' 열쇠로 한 번만 다시 시도한다.
          // (같은 열쇠면 서버의 20초 재사용 유예가 그 사이 기록을 마쳐 새 열쇠를 돌려준다)
          await sleep(REFRESH_RETRY_DELAY_MS);
          const latest = tokenManager.getRefreshToken();
          if (!latest) throw firstError;
          response = await requestRefresh(latest);
        }

        const { access_token, refresh_token: newRefreshToken, expires_at, expires_in } = response.data;
        localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
        localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, access_token);

        if (newRefreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
          localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, newRefreshToken);
        }
        // ⭐ 만료 시각 저장 — 안 하면 매 요청마다 isTokenExpired=true → refresh 무한반복 → 429
        if (expires_at) {
          localStorage.setItem('auth_token_expires_at', expires_at);
        } else if (expires_in) {
          const expiresAt = new Date(Date.now() + expires_in * 1000).toISOString();
          localStorage.setItem('auth_token_expires_at', expiresAt);
        } else {
          // 백엔드가 둘 다 안 주는 비정상 케이스: 1시간 가정 (백엔드 기본값)
          const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
          localStorage.setItem('auth_token_expires_at', expiresAt);
        }
        return access_token;
      } catch (error) {
        // 서버가 열쇠를 '거절'한 경우에만 로그아웃한다. 네트워크 오류·5xx·타임아웃은 토큰을 남긴다.
        if (isAuthRejection(error)) {
          tokenManager.clearTokens();
          markAuthFailure(error);
        }
        throw error;
      } finally {
        // 완료 (성공/실패 무관) 후 in-flight 해제
        refreshInFlight = null;
      }
    })();

    return refreshInFlight;
  },
};